import React from "react";
import { produce } from "immer";
import {
  SSOResponse,
  SingleSignOnViewModel,
  StudentPersonalInfoViewModel,
  VerificationService,
} from "../lib/types/types";
import {
  VerificationStepsEnum,
  getRouteOverride,
  closeTabRef,
  logger,
  setTabRef,
  getTabRef,
  LogoComponent,
} from "../es6";
import { FormFooterComponent as FormFooter } from "./FormFooter/FormFooterComponent";
import { dashed } from "../lib/utils/strings";
import { SingleSignOnPendingComponent } from "./StepPending/SingleSignOnPendingComponent";
import { AnnouncingHeader } from "./AnnouncingHeader/AnnouncingHeader";

export type SingleSignOnMessages = {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  login: React.ReactNode;
  cancel: React.ReactNode;
  subtitle2?: React.ReactNode;
};

export enum ButtonType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

interface SingleSignOnProps {
  verificationService: VerificationService;
  stepName: VerificationStepsEnum.sso | VerificationStepsEnum.thirdPartyLoop;
  messages: SingleSignOnMessages;
  cancelButtonType?: ButtonType;
}

const SingleSignOnComponent = ({
  verificationService,
  stepName,
  messages,
  cancelButtonType = ButtonType.PRIMARY,
}: SingleSignOnProps) => {
  const { programId } = verificationService;
  const viewModel = verificationService.viewModel as SingleSignOnViewModel;
  const previousViewModel =
    (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
  const verificationResponse = verificationService.verificationResponse as SSOResponse;
  const previousVerificationResponse =
    verificationService.previousVerificationResponse || undefined;
  const { verificationId } = verificationService.verificationResponse;
  const isMockingStep = getRouteOverride();
  const dashedStepName = dashed(stepName);
  let timeout;

  const logo = (
    <LogoComponent
      verificationService={verificationService}
      fallbackLogo={
        <img
          className={`sid-${dashedStepName}-logo-default sid-l-lead-image`}
          alt=""
          src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
        />
      }
    />
  );

  const pollForVerificationServiceUpdates = () =>
    setTimeout(() => {
      verificationService.fetchExistingVerificationRequest(
        programId,
        verificationId,
        previousVerificationResponse,
        previousViewModel,
      );
    }, 1000);

  const updateViewModel = (key, value) => {
    const nextState = produce(viewModel, (draft) => {
      (draft[key] as any) = value;
    });
    verificationService.updateViewModel(nextState);
  };

  const cancelSingleSignOnStep = () => {
    closeTabRef();
    const nextState = produce(viewModel, () => {});
    logger.info("SingleSignOnComponent submitting cancel request");
    clearTimeout(timeout);
    verificationService.submitStep(stepName, nextState, verificationService.verificationResponse);
  };

  const updateViewModelAndOpenLoginTab = (key, value) => {
    updateViewModel(key, value);
    openLoginTab();
  };

  const openLoginTab = () => {
    setTabRef(window.open(verificationResponse.loginUrl, "_blank"));
    getTabRef().focus();
  };

  if (viewModel.isSingleSignOnActive && !isMockingStep) {
    timeout = pollForVerificationServiceUpdates();
  }

  if (!viewModel.countryChoice && previousViewModel && previousViewModel.countryChoice) {
    updateViewModel("countryChoice", previousViewModel.countryChoice);
  }

  return (
    <div
      id={`sid-step-${dashedStepName}`}
      className={`sid-${dashedStepName}-container sid-l-container`}
      data-testid={`sid-step-${dashedStepName}`}
    >
      <div role="main">
        {viewModel.isSingleSignOnActive ? (
          <SingleSignOnPendingComponent
            stepName={stepName}
            messages={messages}
            logo={logo}
            cancelSingleSignOn={cancelSingleSignOnStep}
            cancelButtonType={cancelButtonType}
          />
        ) : (
          <div className="sid-header sid-l-horz-center">
            <div className={`sid-${dashedStepName}-logo-container sid-l-horz-center`}>
              <LogoComponent
                verificationService={verificationService}
                fallbackLogo={
                  <img
                    className={`sid-${dashedStepName}-logo-default sid-l-lead-image`}
                    alt=""
                    src="https://assets-resources.sheerid.com/common/images/2018/icons/sso-step.svg"
                  />
                }
              />
            </div>
            <AnnouncingHeader align="center">{messages.title}</AnnouncingHeader>
            <div className="sid-header__subtitle-one">{messages.subtitle}</div>
            <div
              className={`sid-form-region sid-${dashedStepName}-submit sid-submit-wrapper sid-l-space-top-md`}
            >
              <button
                id={`sid-submit-btn-${dashedStepName}`}
                onClick={() => updateViewModelAndOpenLoginTab("isSingleSignOnActive", true)}
                type="submit"
                className={`sid-btn sid-${dashedStepName}-submit-btn sid-btn--dark sid-l-full-width`}
                aria-label="submit"
              >
                {messages.login}
              </button>
            </div>
            <div className="sid-l-space-top-lg" />
          </div>
        )}
      </div>
      <FormFooter verificationService={verificationService} />
    </div>
  );
};

export { SingleSignOnComponent };
